import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  agreements as api,
  type Agreement,
  type AgreementDetail,
  type AgreementStatus,
} from '../../api/agreements';
import { m } from 'motion/react';
import { Handshake } from 'lucide-react';

import { teams as teamsApi } from '../../api/organization';
import { Badge } from '../../components/Badge';
import type { BadgeTone } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmByName } from '../../components/ConfirmByName';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { Card, PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useI18n, useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

import { ProposalWizard } from './ProposalWizard';
import { SafeMarkdown } from '../../components/SafeMarkdown';

/** Solo el tono: la palabra sale de `agreements.status.*`. */
const STATUS_TONE: Record<AgreementStatus, BadgeTone> = {
  draft: 'neutral',
  proposed: 'warning',
  accepted: 'success',
  rejected: 'danger',
  suspended: 'warning',
  expired: 'neutral',
  revoked: 'danger',
};

/**
 * `07 §6`: the agreements screen — listing, proposal assistant, decision
 * screen and activity panel. One route, three modes; the mode is decided by
 * what the manager clicked, not by the URL, matching the conversations screen.
 *
 * The one structural fact the layout leans on: **which side am I?** Every
 * card renders the *other* company as its headline, and the actions differ —
 * an incoming proposal is a decision to make, an outgoing one is a wait.
 */
export function AgreementsScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const client = useQueryClient();
  const [mode, setMode] = useState<'list' | 'wizard'>('list');
  const [selected, setSelected] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<Agreement | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['agreements', companyId],
    queryFn: () => api.list(companyId),
    enabled: companyId !== '',
  });

  const detail = useQuery({
    queryKey: ['agreement', selected],
    queryFn: () => api.show(selected ?? ''),
    enabled: selected !== null,
  });

  const activity = useQuery({
    queryKey: ['agreement-activity', selected],
    queryFn: () => api.activity(selected ?? ''),
    enabled: selected !== null && detail.data?.status === 'accepted',
  });

  const refresh = (id?: string | null) => {
    void client.invalidateQueries({ queryKey: ['agreements', companyId] });
    if (id != null) {
      void client.invalidateQueries({ queryKey: ['agreement', id] });
      void client.invalidateQueries({ queryKey: ['agreement-activity', id] });
    }
  };

  const act = useMutation({
    mutationFn: ({ action, id, reason }: { action: 'propose' | 'withdraw' | 'suspend' | 'resume' | 'revoke'; id: string; reason?: string }) => {
      if (action === 'revoke') return api.revoke(id, reason ?? null);
      return api[action](id);
    },
    onSuccess: (_, variables) => {
      setProblem(null);
      setRevoking(null);
      refresh(variables.id);
    },
    onError: (error) => setProblem(error.message),
  });

  if (mode === 'wizard') {
    return (
      <ProposalWizard
        companyId={companyId}
        onDone={(id) => {
          setMode('list');
          setSelected(id);
        }}
        onCancel={() => setMode('list')}
      />
    );
  }

  const rows = list.data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('agreements.title')}
        actions={<Button onClick={() => setMode('wizard')}>{t('agreements.propose')}</Button>}
      />

      {problem !== null && (
        <p role="alert" className="animate-rise rounded-md border border-danger-line bg-danger-pale p-3 text-sm text-ink">
          {problem}
        </p>
      )}
      {list.isPending && <SkeletonRows rows={3} />}
      {list.isError && <ErrorBanner error={list.error} />}

      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          icon={<Handshake />}
          title={t('agreements.empty')}
          description={t('agreements.emptyHint')}
          action={<Button onClick={() => setMode('wizard')}>{t('agreements.propose')}</Button>}
        />
      )}

      <m.ul variants={STAGGER} initial="hidden" animate="shown" className="space-y-3">
        {rows.map((agreement) => {
          const incoming = agreement.counterpart.id === companyId && agreement.status === 'proposed';
          const other = agreement.initiator.id === companyId ? agreement.counterpart : agreement.initiator;

          return (
            <m.li key={agreement.id} variants={RISE} layout>
              <Card>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 font-semibold text-ink">
                    <Badge tone={STATUS_TONE[agreement.status]} size="sm">
                      {t(`agreements.status.${agreement.status}` as Key)}
                    </Badge>
                    {other.name}
                    {incoming ? (
                      <Badge tone="primary" size="sm">
                        {t('agreements.proposedToYou')}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="mt-1.5 text-sm text-ink-soft">
                    {t('agreements.summary', {
                      title: agreement.title,
                      topics: (agreement.scope.allowed_topics ?? []).join(', ') || t('agreements.none'),
                      delegation:
                        agreement.scope.allow_task_delegation === true ? t('agreements.yes') : t('agreements.no'),
                      repos: (agreement.scope.shared_repositories ?? []).length,
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="secondary" onClick={() => setSelected(selected === agreement.id ? null : agreement.id)}>
                    {incoming ? t('agreements.reviewAndDecide') : selected === agreement.id ? t('common.close') : t('agreements.view')}
                  </Button>
                  {agreement.status === 'draft' && agreement.initiator.id === companyId && (
                    <Button busy={act.isPending} onClick={() => act.mutate({ action: 'propose', id: agreement.id })}>
                      {t('agreements.proposeAction')}
                    </Button>
                  )}
                  {agreement.status === 'proposed' && agreement.initiator.id === companyId && (
                    <Button variant="secondary" busy={act.isPending} onClick={() => act.mutate({ action: 'withdraw', id: agreement.id })}>
                      {t('agreements.withdraw')}
                    </Button>
                  )}
                  {agreement.status === 'accepted' && (
                    <>
                      <Button variant="secondary" busy={act.isPending} onClick={() => act.mutate({ action: 'suspend', id: agreement.id })}>
                        {t('agreements.suspend')}
                      </Button>
                      <Button variant="danger-ghost" onClick={() => setRevoking(agreement)}>
                        {t('agreements.revoke')}
                      </Button>
                    </>
                  )}
                  {agreement.status === 'suspended' && (
                    <>
                      <Button variant="secondary" busy={act.isPending} onClick={() => act.mutate({ action: 'resume', id: agreement.id })}>
                        {t('agreements.resume')}
                      </Button>
                      <Button variant="danger-ghost" onClick={() => setRevoking(agreement)}>
                        {t('agreements.revoke')}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {selected === agreement.id && detail.data !== undefined && (
                <AgreementPanel
                  detail={detail.data}
                  companyId={companyId}
                  companySlug={companySlug}
                  incoming={incoming}
                  activity={activity.data}
                  onDecided={() => refresh(agreement.id)}
                />
              )}
              </Card>
            </m.li>
          );
        })}
      </m.ul>

      {revoking !== null && (
        <ConfirmByName
          name={revoking.title}
          verb={t('agreements.revoke')}
          description={t('agreements.revokeConfirm')}
          busy={act.isPending}
          onConfirm={() => act.mutate({ action: 'revoke', id: revoking.id })}
          onCancel={() => setRevoking(null)}
        />
      )}
    </div>
  );
}

/**
 * The expanded card: `07 §6.3`'s decision screen for an incoming proposal,
 * the activity panel of `07 §7` for one in force.
 */
function AgreementPanel({
  detail,
  companyId,
  companySlug,
  incoming,
  activity,
  onDecided,
}: {
  detail: AgreementDetail;
  companyId: string;
  companySlug: string;
  incoming: boolean;
  activity: { messages_last_7_days: number; top_topics: { topic: string; count: number }[]; delegations: number; blocked_attempts: number } | undefined;
  onDecided: () => void;
}) {
  const t = useT();
  const { plural } = useI18n();
  const client = useQueryClient();
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const ownTeams = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teamsApi.list(companyId),
    enabled: incoming,
  });

  const communicationTeams = useMemo(
    () => (ownTeams.data?.data ?? []).filter((team) => team.type === 'communication' && team.archived_at === null),
    [ownTeams.data],
  );

  const decide = useMutation({
    mutationFn: (accepted: boolean) =>
      accepted ? api.accept(detail.id, teamIds) : api.reject(detail.id, reason === '' ? null : reason),
    onSuccess: () => {
      setProblem(null);
      void client.invalidateQueries({ queryKey: ['agreements', companyId] });
      onDecided();
    },
    onError: (error) => setProblem(error.message),
  });

  const scope = detail.scope;
  const ownSideTeams = detail.teams.filter((team) => team.company_id === companyId);
  const otherSideTeams = detail.teams.filter((team) => team.company_id !== companyId);

  return (
    <div className="mt-4 space-y-4 border-t border-line pt-4 text-sm">
      {problem !== null && (
        <p role="alert" className="animate-rise rounded-md border border-danger-line bg-danger-pale p-3 text-sm text-ink">
          {problem}
        </p>
      )}

      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-ink-soft sm:grid-cols-2">
        <dt>{t('agreements.panel.topics')}</dt>
        <dd className="text-ink">{(scope.allowed_topics ?? []).join(', ') || t('agreements.none')}</dd>
        <dt>{t('agreements.panel.delegation')}</dt>
        <dd className="text-ink">{scope.allow_task_delegation === true ? t('agreements.yes') : t('agreements.no')}</dd>
        <dt>{t('agreements.panel.sharedRepos')}</dt>
        <dd className="text-ink">
          {(scope.shared_repositories ?? []).map((repo) => `${repo.company_slug}/${repo.repository_slug} (${repo.access})`).join(', ') || t('agreements.none')}
        </dd>
        <dt>{t('agreements.panel.fileTransfer')}</dt>
        <dd className="text-ink">
          {scope.allow_file_transfer === true ? t('agreements.panel.allowed') : t('agreements.panel.notAllowed')}
        </dd>
        <dt>{t('agreements.panel.messagesPerDay')}</dt>
        <dd className="text-ink">{scope.message_limits?.max_per_day ?? 100}</dd>
        <dt>{t('agreements.panel.heldForApproval')}</dt>
        <dd className="text-ink">{(scope.requires_approval_for ?? []).join(', ') || t('agreements.nothing')}</dd>
        <dt>{t('agreements.panel.teamsOurSide')}</dt>
        <dd className="text-ink">{ownSideTeams.map((team) => team.team_name).join(', ') || t('agreements.noneYet')}</dd>
        <dt>{t('agreements.panel.teamsTheirSide')}</dt>
        <dd className="text-ink">{otherSideTeams.map((team) => team.team_name).join(', ') || t('agreements.noneYet')}</dd>
      </dl>

      {detail.terms !== null && <SafeMarkdown className="text-ink-soft">{detail.terms}</SafeMarkdown>}
      {scope.confidentiality_note !== undefined && (
        <p className="rounded-sm bg-muted p-2.5 text-ink-soft">🔒 {scope.confidentiality_note}</p>
      )}

      {detail.status === 'rejected' && detail.rejection_reason !== null && (
        <p className="text-ink-soft">{t('agreements.panel.rejected', { reason: detail.rejection_reason })}</p>
      )}

      {detail.status === 'accepted' && (
        <div className="flex flex-wrap items-center gap-4 text-ink-soft">
          <span>{t('agreements.panel.lastSevenDays', { count: activity?.messages_last_7_days ?? '—' })}</span>
          <span>{t('agreements.panel.delegations', { count: activity?.delegations ?? '—' })}</span>
          <span>{t('agreements.panel.blockedAttempts', { count: activity?.blocked_attempts ?? '—' })}</span>
          <Link
            to="/c/$companySlug/conversations"
            params={{ companySlug }}
            className="font-semibold text-primary hover:underline"
          >
            {t('agreements.panel.viewConversations')}
          </Link>
        </div>
      )}

      {incoming && (
        <div className="space-y-3 rounded-md border border-primary/25 bg-primary-pale/40 p-4">
          <p className="font-semibold text-ink">{t('agreements.panel.decide')}</p>
          <p className="leading-relaxed text-ink-soft">
            {t('agreements.panel.decideHint', {
              topics: (scope.allowed_topics ?? []).join(', ') || t('agreements.panel.noTopics'),
            })}
          </p>
          {communicationTeams.length === 0 ? (
            <p className="text-ink-soft">{t('agreements.panel.noCommunicationTeam')}</p>
          ) : (
            communicationTeams.map((team) => (
              <label key={team.id} className="flex cursor-pointer items-center gap-2 text-ink">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={teamIds.includes(team.id)}
                  onChange={(event) =>
                    setTeamIds((current) =>
                      event.target.checked ? [...current, team.id] : current.filter((id) => id !== team.id),
                    )
                  }
                />
                {team.name} · {plural('agreements.panel.members', team.member_count)}
              </label>
            ))
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button busy={decide.isPending} onClick={() => decide.mutate(true)}>
              {t('agreements.panel.accept')}
            </Button>
            <input
              aria-label={t('agreements.panel.rejectionReason')}
              className={cn(fieldStyles, 'w-64 border-line')}
              placeholder={t('agreements.panel.rejectionPlaceholder')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <Button variant="danger-ghost" busy={decide.isPending} onClick={() => decide.mutate(false)}>
              {t('agreements.panel.reject')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
